
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppContext } from '../../App.jsx';
import { X, AlertTriangle, Plus, Trash2, CheckSquare, Square, Info, ShieldAlert, Ban, Percent } from 'lucide-react';

const ProposalItem = ({ proposal, onSelect, isSelected }) => (
    <div
        onClick={() => onSelect(proposal.id)}
        className={`flex items-center justify-between p-3 rounded-2xl border-2 transition-all cursor-pointer ${isSelected ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-100 bg-gray-50'}`}
    >
        <div className="flex items-center min-w-0">
            <div className="mr-3 flex-shrink-0">
                {isSelected ? <CheckSquare className="text-blue-600 w-6 h-6" /> : <Square className="text-gray-300 w-6 h-6" />}
            </div>
            <div className="truncate">
                <p className="font-bold text-gray-800 text-sm truncate">{proposal.items?.[0]?.description || "Orçamento"}</p>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Proposta #{proposal.id.substring(0, 5)}</p>
            </div>
        </div>
        <p className="font-black text-gray-900 ml-3 flex-shrink-0">R$ {proposal.total.toFixed(2)}</p>
    </div>
);

const FinalizeServiceModal = ({ chat, onClose, onSubmit }) => {
    const { showMessage, respondToQuote, getProposalsForChat } = useAppContext();

    const [pendingProposal, setPendingProposal] = useState(null);
    const [acceptedProposals, setAcceptedProposals] = useState([]);
    const [selectedProposalIds, setSelectedProposalIds] = useState([]);
    const [additionalItems, setAdditionalItems] = useState([]);
    const [discount, setDiscount] = useState('');
    const [isLoadingProposals, setIsLoadingProposals] = useState(true);

    const fetchProposals = useCallback(async () => {
        if (!chat?.id || !getProposalsForChat) return;

        setIsLoadingProposals(true);
        const allProposals = await getProposalsForChat(chat.id);

        const pending = allProposals.find(p => p.status === 'pending');
        const accepted = allProposals.filter(p => p.status === 'accepted');

        setPendingProposal(pending);
        setAcceptedProposals(accepted);
        setSelectedProposalIds(accepted.map(p => p.id));
        setIsLoadingProposals(false);
    }, [chat?.id, getProposalsForChat]);

    useEffect(() => {
        fetchProposals();
    }, [fetchProposals]);

    const handleCancelPendingProposal = async () => {
        if (!pendingProposal) return;
        try {
            await respondToQuote(pendingProposal.id, 'canceled');
            setPendingProposal(null);
            showMessage('Orçamento pendente cancelado.', 'success');
            fetchProposals();
        } catch (e) {
            showMessage('Erro ao cancelar orçamento.', 'error');
        }
    };

    const calculations = useMemo(() => {
        const subtotalFromProposals = acceptedProposals.filter(p => selectedProposalIds.includes(p.id)).reduce((acc, p) => acc + p.total, 0);
        const subtotalFromNewItems = additionalItems.reduce((acc, item) => acc + (parseFloat(item.price) || 0), 0);
        const subtotal = subtotalFromProposals + subtotalFromNewItems;
        const total = subtotal - (parseFloat(discount) || 0);
        return { subtotal, total };
    }, [acceptedProposals, selectedProposalIds, additionalItems, discount]);

    const hasUnselectedAccepted = useMemo(() => {
        return acceptedProposals.length > 0 && selectedProposalIds.length < acceptedProposals.length;
    }, [acceptedProposals, selectedProposalIds]);

    const isTotalInvalid = calculations.total < 10;

    const handleSubmitInvoice = () => {
        if (isTotalInvalid) {
            showMessage("O valor mínimo para cobrança é R$ 10,00.", "warning");
            return;
        }
        const serviDetails = chat.participantDetails[chat.serviId];

        const skippedProposals = acceptedProposals
            .filter(p => !selectedProposalIds.includes(p.id))
            .map(p => p.items?.[0]?.description || "Serviço Adicional");

        const finalInvoice = {
            serviId: chat.serviId,
            clientId: chat.clientId,
            serviDetails: {
                name: serviDetails.name,
                companyName: serviDetails.companyName || null,
                photoUrl: serviDetails.photoUrl || null
            },
            includedProposals: acceptedProposals
                .filter(p => selectedProposalIds.includes(p.id))
                .map(p => ({ id: p.id, total: p.total, items: p.items })),
            skippedProposals,
            newItems: additionalItems.filter(item => item.description && parseFloat(item.price) > 0).map(item => ({...item, price: parseFloat(item.price)})),
            discount: parseFloat(discount) || 0,
            subtotal: calculations.subtotal,
            total: calculations.total,
            status: 'Aguardando Pagamento'
        };
        onSubmit(finalInvoice);
    };

    const handleToggleProposal = (id) => setSelectedProposalIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const handleItemChange = (index, field, value) => { const newItems = [...additionalItems]; newItems[index][field] = value; setAdditionalItems(newItems); };
    const handleAddItem = () => setAdditionalItems([...additionalItems, { description: '', price: '' }]);
    const handleRemoveItem = (index) => setAdditionalItems(additionalItems.filter((_, i) => i !== index));

    if (pendingProposal) {
        return (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
                <div className="bg-white rounded-[2rem] w-full max-w-sm relative animate-slide-up flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <header className="p-6 flex flex-col items-center text-center">
                        <div className="bg-yellow-50 p-4 rounded-full mb-4">
                            <AlertTriangle className="w-10 h-10 text-yellow-500" />
                        </div>
                        <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">Orçamento Pendente</h2>
                    </header>
                    <main className="px-6 pb-6 text-sm text-gray-500 text-center leading-relaxed">
                        Cancele o orçamento pendente para gerar a cobrança final.
                    </main>
                    <footer className="p-4 bg-gray-50 border-t grid grid-cols-2 gap-3 rounded-b-[2rem]">
                        <button onClick={onClose} className="w-full py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl">Voltar</button>
                        <button onClick={handleCancelPendingProposal} className="w-full py-3 bg-red-500 text-white font-black rounded-xl shadow-lg uppercase text-xs">Cancelar</button>
                    </footer>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-all" onClick={onClose}>
            <div className="bg-white rounded-[2.5rem] w-full max-w-lg relative animate-slide-up flex flex-col overflow-hidden shadow-2xl" style={{maxHeight: '90vh'}} onClick={(e) => e.stopPropagation()}>
                <header className="p-5 border-b flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-black text-gray-800 leading-tight uppercase tracking-tighter">Finalizar Serviço</h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Gere a cobrança para o pagamento</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors bg-gray-50 border border-gray-100"><X size={20}/></button>
                </header>

                <main className="p-5 flex-grow overflow-y-auto space-y-6">
                    <div>
                        <h3 className="text-[10px] font-black text-gray-400 mb-3 uppercase tracking-[0.2em] ml-1">1. Itens da Cobrança</h3>
                        <div className="space-y-2">
                            {isLoadingProposals ? (
                                <div className="py-10 text-center animate-pulse text-gray-400 font-bold uppercase text-xs tracking-widest">Carregando...</div>
                            ) : acceptedProposals.length > 0 ? (
                                <>
                                    {acceptedProposals.map(p => <ProposalItem key={p.id} proposal={p} isSelected={selectedProposalIds.includes(p.id)} onSelect={handleToggleProposal} />)}

                                    {hasUnselectedAccepted && (
                                        <div className="bg-red-50 p-4 rounded-2xl border border-red-100 flex items-start mt-3 animate-fade-in">
                                            <Ban size={18} className="text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                                            <p className="text-[11px] text-red-800 leading-relaxed font-medium">
                                                <strong className="block mb-0.5 uppercase tracking-widest text-red-700">Atenção SERVI</strong>
                                                Os orçamentos aceitos que você desmarcou serão <b>cancelados definitivamente</b> ao finalizar este atendimento.
                                            </p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="bg-gray-50 p-6 rounded-2xl border border-dashed border-gray-200 text-center italic text-sm text-gray-400">Nenhum orçamento aceito.</div>
                            )}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-[10px] font-black text-gray-400 mb-3 uppercase tracking-[0.2em] ml-1">2. Ajustes e Extras</h3>
                        <div className="space-y-4 p-4 bg-gray-50 rounded-[2rem] border border-gray-100">
                            {additionalItems.map((item, index) => (
                                <div key={index} className="flex items-center space-x-2 animate-slide-right">
                                    <input type="text" value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} placeholder="Descrição" className="flex-grow p-3 text-sm border-2 border-transparent rounded-xl bg-white focus:border-blue-500 outline-none transition-all" />
                                    <div className="relative">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-black text-gray-400">R$</span>
                                        <input type="number" value={item.price} onChange={(e) => handleItemChange(index, 'price', e.target.value)} placeholder="0,00" className="w-24 pl-7 p-3 text-sm border-2 border-transparent rounded-xl bg-white font-black focus:border-blue-500 outline-none transition-all" />
                                    </div>
                                    <button onClick={() => handleRemoveItem(index)} className="p-3 text-red-500"><Trash2 size={18}/></button>
                                </div>
                            ))}
                            <button onClick={handleAddItem} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-2xl text-blue-600 font-bold text-xs">+ ADICIONAR ITEM EXTRA</button>
                            <div className="pt-2 border-t border-gray-200">
                                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Aplicar Desconto (R$)</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-red-400">R$</span>
                                    <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0,00" className="w-full pl-9 p-3 border-2 border-transparent rounded-xl bg-white font-black text-red-600 focus:border-red-200 outline-none transition-all" />
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                <footer className="p-6 bg-gray-50 border-t space-y-4 shrink-0">
                    {/* AVISO CIRÚRGICO DE REPASSE (EXCLUSIVO SERVI) */}
                    {!isTotalInvalid && (
                        <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex items-start animate-fade-in">
                            <Percent size={18} className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                            <div className="text-left">
                                <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest leading-none">Apoio à Plataforma</p>
                                <p className="text-[11px] text-blue-800 font-medium mt-1 leading-tight">
                                    Ao finalizar, uma contribuição de 10% será calculada. As instruções de repasse serão enviadas para suas <b>Notificações</b>.
                                </p>
                            </div>
                        </div>
                    )}

                    {isTotalInvalid && calculations.total > 0 && (
                        <div className="bg-red-50 p-3 rounded-xl border border-red-100 flex items-start animate-fade-in">
                            <ShieldAlert size={16} className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-[10px] text-red-700 font-bold leading-tight">
                                VALOR INSUFICIENTE: O valor mínimo para transações na T-SERVI é de R$ 10,00. Adicione itens extras para prosseguir.
                            </p>
                        </div>
                    )}

                    <div className="bg-white p-5 rounded-[2rem] border-2 border-blue-50 shadow-lg">
                        <div className="flex justify-between items-center">
                            <span className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Total Final</span>
                            <span className={`text-3xl font-black leading-none ${isTotalInvalid ? 'text-red-500' : 'text-gray-900'}`}>
                                <span className="text-sm mr-1 font-bold">R$</span>
                                {calculations.total.toFixed(2)}
                            </span>
                        </div>
                    </div>

                    <button
                        onClick={handleSubmitInvoice}
                        disabled={isTotalInvalid}
                        className="w-full py-5 bg-green-600 text-white font-black text-lg rounded-[1.5rem] transition shadow-xl shadow-green-100 hover:bg-green-700 disabled:bg-gray-300 disabled:shadow-none uppercase tracking-widest active:scale-[0.98]"
                    >
                        Enviar Cobrança
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default FinalizeServiceModal;
